import React from 'react';
import { Button, Checkbox, DatePicker, Select, Space, Tag } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { IOperator, IPlan, OPTIONAL_MODULES, OptionalModule } from '../../../types/models/tenancy';

const Wrapper = styled.div`
  .section {
    margin-bottom: 22px;
  }

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--ancana-font-light);
    margin-bottom: 8px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .hint {
    font-size: 12px;
    color: var(--ancana-font-light);
    margin: 4px 0 0;
  }

  .row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 12px 16px;
    margin-bottom: 16px;
  }
`;

const DATE = 'YYYY-MM-DD';

export interface PlanFieldsProps {
  value: Partial<IOperator>;
  onChange: (draft: Partial<IOperator>) => void;
  plans: IPlan[];
  /** The saved row, to say how a trial stands; absent when creating. */
  saved?: IOperator;
}

/**
 * Plan, trial and modules: what a tenant has (playbook D15).
 *
 * The plan is where the modules START: picking one copies its modules into the
 * switches below, which staff can then change for a custom deal. The switches
 * are what every check reads. A trial is an end date on top of the plan; the
 * day after it, the tenant is suspended (data kept). Extending moves the date
 * and reactivates; converting clears it. The plan and modules stay either way.
 */
const PlanFields = ({ value, onChange, plans, saved }: PlanFieldsProps) => {
  const set = (patch: Partial<IOperator>) => onChange({ ...value, ...patch });
  const isNew = !saved;
  const onTrial = !!value.trial_ends_at;
  const ended = !!value.trial_ends_at && moment(value.trial_ends_at).isBefore(moment(), 'day');
  const plan = plans.find(p => p.id === value.plan);

  const pickPlan = (id: number) => {
    const picked = plans.find(p => p.id === id);
    if (!picked) return;
    set({
      plan: id,
      modules: [...picked.modules],
      // A new trial's length follows the plan.
      ...(isNew && onTrial
        ? { trial_ends_at: moment().add(picked.trial_days, 'days').format(DATE) } : {}),
    });
  };

  // Moving a trial's end into the future brings a suspended tenant back.
  const setEnd = (date: moment.Moment | null) => set({
    trial_ends_at: date ? date.format(DATE) : null,
    ...(date && !date.isBefore(moment(), 'day') && value.status === 'suspended' ? { status: 'active' } : {}),
  });

  const extend = (days: number) => {
    const from = ended || !value.trial_ends_at ? moment() : moment(value.trial_ends_at);
    setEnd(from.add(days, 'days'));
  };

  const convert = () => set({
    trial_ends_at: null,
    ...(value.status === 'suspended' ? { status: 'active' } : {}),
  });

  let trialStatus: React.ReactNode = <Tag>Contract</Tag>;
  if (onTrial && ended) trialStatus = <Tag color="red">Trial ended</Tag>;
  else if (onTrial && saved?.trial_ends_at === value.trial_ends_at && saved?.trial_days_left != null) {
    trialStatus = <Tag color="blue">Trial · {saved.trial_days_left} days left</Tag>;
  } else if (onTrial) trialStatus = <Tag color="blue">Trial</Tag>;

  return (
    <Wrapper>
      <div className="section">
        <div className="section-label">Plan and trial</div>
        <div className="row">
          <div>
            <div className="field-label">Plan</div>
            <Select<number>
              value={value.plan ?? undefined}
              onChange={pickPlan}
              placeholder={plans.length ? 'Default trial plan' : 'No plans yet'}
              style={{ width: '100%' }}
              options={plans.map(p => ({ value: p.id, label: p.name }))}
            />
            <p className="hint">Picking a plan resets the modules below to its defaults.</p>
          </div>
          <div>
            <div className="field-label">Trial {trialStatus}</div>
            {onTrial ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <DatePicker
                  value={moment(value.trial_ends_at)}
                  onChange={setEnd}
                  allowClear={false}
                  style={{ width: '100%' }}
                />
                <Space wrap>
                  <Button size="small" onClick={() => extend(14)}>+14 days</Button>
                  <Button size="small" onClick={() => extend(30)}>+30 days</Button>
                  <Button size="small" onClick={convert}>{isNew ? 'No trial' : 'Convert to contract'}</Button>
                </Space>
              </Space>
            ) : (
              <Button size="small" onClick={() => extend(plan?.trial_days ?? 30)}>Start a trial</Button>
            )}
            <p className="hint">
              {onTrial
                ? 'The last day they can work. The day after, the tenant is suspended; its data is kept.'
                : 'Not on a trial: the tenant stays active until someone suspends it.'}
              {value.status !== saved?.status && value.status === 'active' && saved ? ' Saving reactivates it.' : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-label">Modules</div>
        <Checkbox.Group
          value={value.modules ?? plan?.modules ?? OPTIONAL_MODULES.map(m => m.key)}
          onChange={modules => set({ modules: modules as OptionalModule[] })}
          options={OPTIONAL_MODULES.map(m => ({ value: m.key, label: m.label }))}
        />
        <p className="hint">
          Properties, owners, calendar and finances are always on. Switched-off modules disappear
          from the console and their API answers empty.
        </p>
      </div>
    </Wrapper>
  );
};

export default PlanFields;
